import { useEscapeClose } from '@/shared/hooks/useEscapeClose';
import { AlertTriangle, FileText, X } from 'lucide-react';
import type { TemplateRow } from '../api';

interface TemplateComponent {
  type?: string;
  format?: string;
  text?: string;
  /** Mídia de exemplo aprovada pela Meta (header IMAGE/VIDEO/DOCUMENT). */
  example?: { header_handle?: string[]; header_text?: string[] };
  buttons?: Array<{ type?: string; text?: string; url?: string; phone_number?: string }>;
}

function buttonIcon(type: string): string {
  const t = (type ?? '').toUpperCase();
  if (t === 'URL') return '🔗';
  if (t === 'PHONE_NUMBER') return '📞';
  return '↩';
}

/** Label + cor do status do template (mesmo esquema da tabela de Templates). */
const STATUS_LABEL: Record<TemplateRow['status'], { label: string; classes: string }> = {
  approved: { label: 'Aprovado', classes: 'bg-success-50 text-success-700' },
  pending: { label: 'Pendente', classes: 'bg-warning-50 text-warning-700' },
  rejected: { label: 'Rejeitado', classes: 'bg-danger-50 text-danger-700' },
  disabled: { label: 'Desabilitado', classes: 'bg-n-100 text-n-600' },
  paused: { label: 'Pausado', classes: 'bg-warning-50 text-warning-700' },
};

/** Realça `{{N}}` (variável sem valor) num chip âmbar — aqui é só VISUALIZAÇÃO
 *  do template como está, sem preencher variáveis. */
function realcaVars(text: string): React.ReactNode[] {
  return text.split(/(\{\{\d+\}\})/g).map((part, i) => {
    const key = `${i}-${part}`;
    if (/^\{\{\d+\}\}$/.test(part)) {
      return (
        <span key={key} className="rounded bg-warning-100 px-1 text-warning-800">
          {part}
        </span>
      );
    }
    return <span key={key}>{part}</span>;
  });
}

/**
 * Prévia visual de um template HSM (estilo bolha WhatsApp). Renderiza os
 * `components` crus da Meta — header (mídia de exemplo ou texto), corpo, rodapé
 * e botões. Mesmo padrão visual do preview do wizard de campanha, mas em modo
 * leitura (mostra `{{N}}` em vez de substituir).
 */
export function TemplatePreviewModal({
  template,
  onClose,
}: {
  template: TemplateRow;
  onClose: () => void;
}) {
  const components = (template.components ?? []) as TemplateComponent[];
  const header = components.find((c) => (c.type ?? '').toUpperCase() === 'HEADER');
  const body = components.find((c) => (c.type ?? '').toUpperCase() === 'BODY');
  const footer = components.find((c) => (c.type ?? '').toUpperCase() === 'FOOTER');
  const buttonsComp = components.find((c) => (c.type ?? '').toUpperCase() === 'BUTTONS');
  const headerFormat = (header?.format ?? 'TEXT').toUpperCase();
  const headerIsText = headerFormat === 'TEXT';
  const headerMediaUrl = header?.example?.header_handle?.[0] ?? null;

  useEscapeClose(true, onClose);

  return (
    // biome-ignore lint/a11y/useKeyWithClickEvents: backdrop click é UX padrão; Esc fecha via useEscapeClose
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      role="dialog"
      aria-modal="true"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="flex max-h-[85vh] w-full max-w-[420px] flex-col overflow-hidden rounded-lg bg-n-0 shadow-xl">
        <header className="flex flex-shrink-0 items-center justify-between border-n-200 border-b px-5 py-3">
          <h3 className="font-semibold text-[15px] text-n-900">Prévia do template</h3>
          <button
            type="button"
            onClick={onClose}
            className="flex h-8 w-8 items-center justify-center rounded-md text-n-500 hover:bg-n-100"
          >
            <X size={16} />
          </button>
        </header>

        <div className="min-h-0 flex-1 overflow-y-auto p-5">
          <div className="mb-3 flex flex-wrap items-center gap-2 text-[11px] text-n-500">
            <span className="rounded bg-n-100 px-1.5 py-0.5 font-mono text-n-700">
              {template.name}
            </span>
            <span>{template.category}</span>
            <span>{template.language}</span>
            <span>·</span>
            <span
              className={`rounded-pill px-2 py-0.5 font-semibold ${STATUS_LABEL[template.status].classes}`}
            >
              {STATUS_LABEL[template.status].label}
            </span>
          </div>

          {/* Bolha estilo WhatsApp (verde outbound). force-light: o verde é fixo
              e não inverte no dark. */}
          <div className="force-light overflow-hidden rounded-lg bg-[#dcf8c6] text-[13px] text-n-900 shadow-sm">
            {header && !headerIsText && (
              <div className="bg-black/5">
                {headerFormat === 'IMAGE' && headerMediaUrl ? (
                  <img
                    src={headerMediaUrl}
                    alt="Cabeçalho do template"
                    className="max-h-56 w-full object-cover"
                  />
                ) : headerFormat === 'VIDEO' && headerMediaUrl ? (
                  // biome-ignore lint/a11y/useMediaCaption: preview de template, sem legenda
                  <video src={headerMediaUrl} controls className="max-h-56 w-full" />
                ) : headerFormat === 'DOCUMENT' ? (
                  // Documento: mostra o nome (se o admin curou a mídia do header)
                  // e vira link pro exemplo aprovado na Meta, pra abrir/baixar.
                  headerMediaUrl ? (
                    <a
                      href={headerMediaUrl}
                      target="_blank"
                      rel="noreferrer"
                      className="flex items-center gap-2 p-3 text-[13px] text-[#1f7aad] hover:underline"
                    >
                      <FileText size={18} className="flex-shrink-0" />
                      <span className="truncate">Abrir documento de exemplo</span>
                    </a>
                  ) : (
                    <div className="flex items-center gap-2 p-3 text-[12px] text-n-500">
                      <FileText size={16} className="flex-shrink-0" />
                      <span className="truncate">Documento no cabeçalho</span>
                    </div>
                  )
                ) : (
                  <div className="flex h-28 items-center justify-center text-[11px] text-n-500">
                    {`[ ${headerFormat} ]`}
                  </div>
                )}
              </div>
            )}
            <div className="p-3">
              {header && headerIsText && header.text && (
                <div className="mb-1.5 font-bold leading-snug">{realcaVars(header.text)}</div>
              )}
              {body?.text && (
                <div className="whitespace-pre-wrap leading-snug">{realcaVars(body.text)}</div>
              )}
              {footer?.text && <div className="mt-2 text-[11px] text-n-500">{footer.text}</div>}
            </div>
            {buttonsComp?.buttons && buttonsComp.buttons.length > 0 && (
              <div className="border-black/10 border-t">
                {buttonsComp.buttons.map((b, i) => (
                  <div
                    key={`${b.type}-${i}-${b.text}`}
                    className="flex items-center justify-center gap-1.5 border-black/5 border-t py-2 font-semibold text-[13px] text-[#1f7aad] first:border-t-0"
                  >
                    <span className="text-[12px]">{buttonIcon(b.type ?? '')}</span>
                    {b.text ?? 'Botão'}
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Aviso abaixo da bolha: a mídia mostrada é o EXEMPLO cadastrado no
              template na Meta — não necessariamente a enviada (essa é a
              definida no cabeçalho). Só aparece quando há header de mídia. */}
          {header && !headerIsText && (
            <div className="mt-2">
              <span className="inline-flex items-center gap-1.5 rounded-md bg-warning-50 px-2 py-1 text-[11px] font-medium text-warning-700">
                <AlertTriangle size={12} className="flex-shrink-0" />
                Mídia de exemplo cadastrada no template na Meta
              </span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
